import { useCallback, useEffect, useMemo, useState } from 'react';
import { usePatientVirtualAppointments } from '../../../../hooks/useVirtualAppointments';
import { resolveDocImages } from '../../../../utils/docImageResolution';
import {
    applyImageUrlsToDrafts,
    applyImageUrlsToDtos
} from '../../../virtual-appointments/virtualAppointmentImages';
import {
    savedTriggersOf,
    type SavedTriggers
} from '../../../virtual-appointments/virtualAppointmentTriggerAhead';
import {
    hydratePlans,
    savedNogoCounts,
    type HydratedPlans,
    type HydratePlansInput,
    type ResumedDraftPlans,
    type SavedNogoCounts,
    type VirtualAppointmentLoadStatus
} from './virtualAppointmentPlanMerge';

const IMAGE_SOURCE = 'ClinicalWizard.resolveVirtualAppointmentImages';

interface UseVirtualAppointmentHydrationParams {
    patientId: string;
    /** The edited document's or resumed draft's group; undefined for a new document. */
    groupId: string | undefined;
    draft: ResumedDraftPlans;
    legacyNote: string;
}

interface VirtualAppointmentHydration {
    hydrated: HydratedPlans | null;
    loadStatus: VirtualAppointmentLoadStatus;
    retry: () => void;
    /** From the live server read, so the counts follow new scans. */
    nogoCounts: SavedNogoCounts;
    /** The saved planned VAs' triggers, by id: what a row counts as changed against. */
    savedTriggers: SavedTriggers;
}

async function hydrateWithImages(
    patientId: string,
    input: HydratePlansInput
): Promise<HydratedPlans> {
    const base = hydratePlans(input);
    const docs = [
        ...base.planned.map((d) => d.descriptionDoc),
        ...base.frozen.flatMap((va) => (va.descriptionDoc ? [va.descriptionDoc] : []))
    ];
    const urls = await resolveDocImages(patientId, docs, IMAGE_SOURCE);
    return {
        ...base,
        planned: applyImageUrlsToDrafts(base.planned, urls),
        frozen: applyImageUrlsToDtos(base.frozen, urls)
    };
}

// Settles once: the first fresh server read (or none, for a new document) seeds the planner.
export function useVirtualAppointmentHydration({
    patientId,
    groupId,
    draft,
    legacyNote
}: UseVirtualAppointmentHydrationParams): VirtualAppointmentHydration {
    const needsServer = groupId !== undefined;
    const query = usePatientVirtualAppointments(
        patientId,
        { groupId },
        { enabled: needsServer, fresh: true }
    );
    const [hydrated, setHydrated] = useState<HydratedPlans | null>(null);

    const serverReady = query.isSuccess && query.isFetchedAfterMount;
    const server = needsServer ? (serverReady ? query.data : undefined) : null;

    useEffect(() => {
        if (hydrated !== null || server === undefined) return;
        let cancelled = false;
        void hydrateWithImages(patientId, { draft, server, legacyNote }).then((result) => {
            if (!cancelled) setHydrated((prev) => prev ?? result);
        });
        return () => {
            cancelled = true;
        };
    }, [patientId, draft, server, legacyNote, hydrated]);

    const { refetch } = query;
    const retry = useCallback(() => void refetch(), [refetch]);
    const nogoCounts = useMemo(
        () => savedNogoCounts(needsServer ? query.data : undefined),
        [needsServer, query.data]
    );
    const savedTriggers = useMemo(
        () => savedTriggersOf(needsServer ? query.data : undefined),
        [needsServer, query.data]
    );
    const loadStatus: VirtualAppointmentLoadStatus =
        hydrated !== null ? 'ready' : query.isError && !query.isFetching ? 'error' : 'loading';
    return { hydrated, loadStatus, retry, nogoCounts, savedTriggers };
}
